import React, { useState } from 'react';
import { X } from 'lucide-react';

const CreateEventModal = ({ selectedDate, onClose, onConfirm }) => {
  const [formData, setFormData] = useState({
    title: '',
    start_date: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
    end_date: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
    event_type: 'competition',
    color: '#f44336',  // Rød for stævner
    description: '',
    blocks_booking: true
  });

  const eventTypes = [
    { value: 'competition', label: 'Competition / Stævne', color: '#f44336' },  // Rød
    { value: 'vacation', label: 'Vacation / Ferie', color: '#ff9800' },  // Orange
    { value: 'blocked', label: 'Blocked / Blokeret', color: '#9e9e9e' },  // Grå
    { value: 'other', label: 'Other / Andet', color: '#2196f3' }  // Blå
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(formData);
  };

  const handleEventTypeChange = (type) => {
    const selectedType = eventTypes.find(t => t.value === type);
    setFormData({
      ...formData,
      event_type: type,
      color: selectedType?.color || '#ff9800'
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '10px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: '1px solid #e5e5e5'
        }}>
          <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Create Admin Event</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {/* Title */}
          <div className="form-group">
            <label className="form-label">Event Title *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Dressage Competition, Summer Vacation"
              required
            />
          </div>

          {/* Event Type */}
          <div className="form-group">
            <label className="form-label">Event Type *</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {eventTypes.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleEventTypeChange(type.value)}
                  style={{
                    padding: '0.75rem',
                    border: formData.event_type === type.value ? `2px solid ${type.color}` : '1px solid #e5e5e5',
                    borderRadius: '5px',
                    background: formData.event_type === type.value ? `${type.color}15` : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div style={{
                    width: '15px',
                    height: '15px',
                    borderRadius: '3px',
                    background: type.color
                  }} />
                  <span style={{ fontSize: '0.9rem' }}>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Start Date *</label>
              <input
                type="date"
                className="form-input"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">End Date *</label>
              <input
                type="date"
                className="form-input"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description / Notes</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Add any notes or details about this event..."
            />
          </div>

          {/* Blocks Booking Checkbox */}
          <div className="form-group">
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              cursor: 'pointer',
              padding: '1rem',
              background: '#fff3e0',
              borderRadius: '5px',
              border: '1px solid #ff9800'
            }}>
              <input
                type="checkbox"
                checked={formData.blocks_booking}
                onChange={(e) => setFormData({ ...formData, blocks_booking: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                  Block Bookings on These Dates
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  When enabled, customers cannot book lessons during this event
                </div>
              </div>
            </label>
          </div>

          {/* Color Preview */}
          <div style={{
            padding: '1rem',
            background: '#f5f5f5',
            borderRadius: '5px',
            marginBottom: '1rem'
          }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Calendar Preview:
            </div>
            <div style={{
              display: 'inline-block',
              padding: '0.5rem 1rem',
              background: formData.color,
              color: 'white',
              borderRadius: '5px',
              fontSize: '0.85rem',
              fontWeight: 500
            }}>
              {formData.title || 'Event Title'}
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#e5e5e5',
                color: '#333',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cta-button"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Create Event
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventModal;
